import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Cloud, RefreshCw, KeyRound, Search, Bell, Plug, GitCompare, Send, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import CredentialsDialog from "../components/CredentialsDialog";
import DeployDialog from "../components/DeployDialog";

const META = {
  aws: { name: "Amazon Web Services", color: "#ff9900", icon: "/cloud-icons/aws-icon.svg" },
  azure: { name: "Microsoft Azure", color: "#0089d6", icon: "/cloud-icons/azure-icon.svg" },
  gcp: { name: "Google Cloud Platform", color: "#34a853", icon: "/cloud-icons/gcp-icon.svg" },
  oracle: { name: "Oracle Cloud", color: "#f80000", icon: "/cloud-icons/oracle-icon.svg" },
  cloudflare: { name: "Cloudflare", color: "#f48120" },
  virtualexpanse: { name: "VirtualExpanse", color: "#7c3aed" },
  omnissa: { name: "Omnissa / OIC", color: "#10b981" },
};

export default function Connectors() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [creds, setCreds] = useState([]);
  const [busy, setBusy] = useState("");
  const [credDialog, setCredDialog] = useState(null);
  const [bulkPushFor, setBulkPushFor] = useState(null); // provider string when bulk-push dialog open
  const [policies, setPolicies] = useState([]);

  const load = useCallback(async () => {
    try {
      const [c, k, p] = await Promise.all([
        api.get("/connectors"),
        user?.role === "admin" ? api.get("/cloud-credentials") : Promise.resolve({ data: [] }),
        api.get("/policies"),
      ]);
      setItems(c.data);
      setCreds(k.data || []);
      setPolicies(p.data || []);
    } catch (err) {
      console.error("connectors load failed", err);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const isConfigured = (p) => creds.some((c) => c.provider === p);

  const sync = async (provider) => {
    setBusy(`sync-${provider}`);
    try {
      const { data } = await api.post(`/connectors/${provider}/sync`);
      if (data?.status === "error") {
        toast.error(`${provider.toUpperCase()}: ${data.error_message || "sync failed"}`);
      } else if (data?.status === "unconfigured") {
        toast.message(`${provider.toUpperCase()}: no credentials configured`);
      } else {
        toast.success(`${provider.toUpperCase()} synced (${data?.latency_ms ?? "?"}ms)`);
      }
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <Layout title="Cloud Connectors" subtitle="MULTI-CLOUD">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {items.map((c) => (
          <ConnectorCard
            key={c.provider}
            connector={c}
            configured={isConfigured(c.provider)}
            isAdmin={user?.role === "admin"}
            busy={busy}
            onConfigure={() => setCredDialog(c.provider)}
            onSync={() => sync(c.provider)}
            onBulkPush={() => setBulkPushFor(c.provider)}
            setBusy={setBusy}
          />
        ))}
      </div>

      <CredentialsDialog
        open={!!credDialog} provider={credDialog}
        onClose={() => setCredDialog(null)}
        onSaved={() => load()}
      />

      {bulkPushFor && (
        <BulkPushDialog
          open={!!bulkPushFor}
          onOpenChange={(v) => { if (!v) setBulkPushFor(null); }}
          provider={bulkPushFor}
          policies={policies}
        />
      )}
    </Layout>
  );
}

function ConnectorCard({ connector: c, configured, isAdmin, busy, onConfigure, onSync, onBulkPush, setBusy }) {
  const m = META[c.provider] || { name: c.provider };
  const [discovered, setDiscovered] = useState([]);
  const [drift, setDrift] = useState(null);
  const [tab, setTab] = useState("status");

  const loadDiscovered = useCallback(async () => {
    try {
      const { data } = await api.get(`/connectors/${c.provider}/discovered`);
      setDiscovered(data || []);
    } catch (e) { /* not configured / no discover yet — fine */ }
  }, [c.provider]);

  const loadDrift = useCallback(async () => {
    try {
      const { data } = await api.get(`/connectors/${c.provider}/drift`);
      setDrift(data);
    } catch (e) { setDrift(null); }
  }, [c.provider]);

  useEffect(() => {
    if (tab === "discovered") loadDiscovered();
    if (tab === "drift") { loadDiscovered(); loadDrift(); }
  }, [tab, loadDiscovered, loadDrift]);

  const discover = async () => {
    setBusy(`disc-${c.provider}`);
    try {
      const { data } = await api.post(`/connectors/${c.provider}/discover`);
      toast.success(`Pulled ${data.count} rules from ${c.provider.toUpperCase()}`);
      await loadDiscovered();
      if (tab === "drift") await loadDrift();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const ingest = async () => {
    setBusy(`ing-${c.provider}`);
    try {
      const { data } = await api.post(`/connectors/${c.provider}/ingest-alerts`, { limit: 50 });
      toast.success(`Ingested ${data.ingested} ${c.provider.toUpperCase()} alerts`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const pushOne = async (policyId) => {
    try {
      await api.post(`/connectors/${c.provider}/push`, { policy_id: policyId, dry_run: false });
      toast.success(`Pushed policy to ${c.provider.toUpperCase()}`);
      await loadDrift();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={`connector-card-${c.provider}`} className="border border-zinc-800 rounded-sm bg-zinc-900/60 fade-up relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: m.color }} />
      <div className="p-5 pb-3 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm border border-zinc-800 grid place-items-center" style={{ background: `${m.color}15` }}>
            {m.icon
              ? <img src={m.icon} alt={m.name} className="w-6 h-6 object-contain" />
              : <Cloud className="w-5 h-5" style={{ color: m.color }} />}
          </div>
          <div>
            <div className="font-display font-bold text-lg tracking-tight uppercase">{c.provider}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{m.name}</div>
          </div>
        </div>
        <div className="flex flex-col gap-1.5 items-end">
          <span className={"text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm flex items-center gap-1.5 " +
            (c.status === "healthy" ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
              : c.status === "degraded" ? "text-amber-400 border-amber-500/40 bg-amber-500/10"
              : c.status === "unconfigured" ? "text-zinc-400 border-zinc-700 bg-zinc-800/40"
              : "text-red-400 border-red-500/40 bg-red-500/10")}>
            <span className={c.status === "healthy" ? "live-dot" : c.status === "degraded" ? "live-dot amber" : "live-dot red"} />
            {c.status || "unknown"}
          </span>
          <span data-testid={`creds-status-${c.provider}`} className={"text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 border rounded-sm " +
            (configured ? "text-cyan-400 border-cyan-500/40 bg-cyan-500/5" : "text-zinc-500 border-zinc-700")}>
            {configured ? "● CREDENTIALS LIVE" : "○ NO CREDENTIALS"}
          </span>
        </div>
      </div>

      {c.error_message && (
        <div className="mx-5 mb-2 px-3 py-1.5 border border-red-500/40 bg-red-500/10 text-red-300 text-[11px] font-mono flex items-center gap-2 rounded-sm">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          <span className="whitespace-normal break-words">{c.error_message}</span>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="px-5 pb-5">
        <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm h-auto p-0.5">
          <TabsTrigger value="status" className="text-[10px] font-mono uppercase tracking-widest data-[state=active]:bg-zinc-800">Status</TabsTrigger>
          <TabsTrigger value="discovered" className="text-[10px] font-mono uppercase tracking-widest data-[state=active]:bg-zinc-800">Discovered</TabsTrigger>
          <TabsTrigger value="drift" className="text-[10px] font-mono uppercase tracking-widest data-[state=active]:bg-zinc-800">Drift</TabsTrigger>
        </TabsList>

        <TabsContent value="status" className="mt-3">
          <div className="grid grid-cols-3 gap-2 text-center mb-3">
            <Stat label="LATENCY" v={c.latency_ms != null ? `${c.latency_ms}ms` : "—"} />
            <Stat label="LAST SYNC" v={c.last_sync ? c.last_sync.slice(11, 19) : "—"} />
            <Stat label="LAST OK" v={c.last_ok ? c.last_ok.slice(11, 19) : "—"} />
          </div>
          {isAdmin && (
            <div className="grid grid-cols-2 gap-2">
              <Button data-testid={`configure-${c.provider}`} onClick={onConfigure} variant="outline" className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 rounded-sm gap-2">
                <KeyRound className="w-3.5 h-3.5" /> {configured ? "UPDATE" : "CONFIGURE"}
              </Button>
              <Button data-testid={`sync-${c.provider}`} onClick={onSync} disabled={busy === `sync-${c.provider}`} variant="outline" className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 rounded-sm gap-2">
                <RefreshCw className={`w-3.5 h-3.5 ${busy === `sync-${c.provider}` ? "animate-spin" : ""}`} /> SYNC
              </Button>
              <Button data-testid={`discover-${c.provider}`} onClick={discover} disabled={!configured || busy === `disc-${c.provider}`} className="bg-cyan-500 hover:bg-cyan-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-zinc-950 rounded-sm gap-2">
                <Search className="w-3.5 h-3.5" /> DISCOVER
              </Button>
              <Button data-testid={`ingest-${c.provider}`} onClick={ingest} disabled={!configured || busy === `ing-${c.provider}`} className="bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-zinc-950 rounded-sm gap-2">
                <Bell className="w-3.5 h-3.5" /> INGEST
              </Button>
              <Button data-testid={`bulkpush-${c.provider}`} onClick={onBulkPush} disabled={!configured} className="col-span-2 bg-emerald-500 hover:bg-emerald-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-zinc-950 rounded-sm gap-2">
                <Send className="w-3.5 h-3.5" /> BULK PUSH POLICIES
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="discovered" className="mt-3">
          <DiscoveredTable rules={discovered} provider={c.provider} />
        </TabsContent>

        <TabsContent value="drift" className="mt-3">
          <DriftView drift={drift} onPush={pushOne} provider={c.provider} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DiscoveredTable({ rules, provider }) {
  if (!rules || rules.length === 0) {
    return <div className="text-zinc-500 text-xs font-mono py-6 text-center">no discovered rules cached. run DISCOVER on the Status tab.</div>;
  }
  return (
    <div className="border border-zinc-800 rounded-sm overflow-hidden">
      <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 bg-zinc-900/60 border-b border-zinc-800 flex items-center gap-2">
        <Plug className="w-3 h-3 text-cyan-400" /> {rules.length} rules in {provider.toUpperCase()}
      </div>
      <div className="overflow-x-auto max-h-72">
        <table className="w-full text-xs">
          <thead className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 bg-zinc-900/40 sticky top-0">
            <tr>
              <th className="text-left px-3 py-1.5">Name</th>
              <th className="text-left px-3 py-1.5">Dir</th>
              <th className="text-left px-3 py-1.5">Action</th>
              <th className="text-left px-3 py-1.5">Proto</th>
              <th className="text-left px-3 py-1.5">Ports</th>
              <th className="text-left px-3 py-1.5">Sources</th>
            </tr>
          </thead>
          <tbody data-testid={`discovered-${provider}`}>
            {rules.map((r, i) => (
              <tr key={r.discover_id || i} className="border-t border-zinc-800/70 font-mono text-[11px]">
                <td className="px-3 py-1.5 text-zinc-200 whitespace-normal break-words max-w-[160px]">{r.name}</td>
                <td className="px-3 py-1.5 uppercase text-zinc-400">{r.direction}</td>
                <td className="px-3 py-1.5 uppercase text-amber-400">{r.action}</td>
                <td className="px-3 py-1.5 uppercase text-cyan-400">{r.protocol}</td>
                <td className="px-3 py-1.5 text-zinc-300">{r.from_port}-{r.to_port}</td>
                <td className="px-3 py-1.5 text-zinc-300 whitespace-normal break-words max-w-[200px]">{(r.sources || []).join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DriftView({ drift, onPush, provider }) {
  if (!drift) return <div className="text-zinc-500 text-xs font-mono py-6 text-center">no drift data. run DISCOVER first, then check this tab.</div>;
  const counts = drift.counts || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <DriftStat label="MATCHED" v={counts.matched ?? 0} color="text-emerald-400 border-emerald-500/40 bg-emerald-500/10" />
        <DriftStat label="ONLY IN CLOUD" v={counts.only_in_cloud ?? 0} color="text-amber-400 border-amber-500/40 bg-amber-500/10" />
        <DriftStat label="ONLY IN RUSTWALL" v={counts.only_in_rustwall ?? 0} color="text-cyan-400 border-cyan-500/40 bg-cyan-500/10" />
      </div>
      {drift.only_in_rustwall?.length > 0 && (
        <DriftSection
          title={<><GitCompare className="w-3 h-3" /> Only in RustWall — push to {provider.toUpperCase()}</>}
          rows={drift.only_in_rustwall}
          render={(p) => (
            <>
              <td className="px-3 py-1.5 text-zinc-200 whitespace-normal break-words max-w-[160px]">{p.name}</td>
              <td className="px-3 py-1.5 uppercase text-zinc-400">{p.direction}</td>
              <td className="px-3 py-1.5 uppercase text-amber-400">{p.action}</td>
              <td className="px-3 py-1.5 uppercase text-cyan-400">{p.protocol}</td>
              <td className="px-3 py-1.5 text-zinc-300">{p.port}</td>
              <td className="px-3 py-1.5 text-right">
                <button
                  data-testid={`drift-push-${p.policy_id}`}
                  onClick={() => onPush(p.policy_id)}
                  className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 rounded-sm"
                >push now</button>
              </td>
            </>
          )}
        />
      )}
      {drift.only_in_cloud?.length > 0 && (
        <DriftSection
          title={<>Only in {provider.toUpperCase()} — not managed by RustWall</>}
          rows={drift.only_in_cloud}
          render={(r) => (
            <>
              <td className="px-3 py-1.5 text-zinc-200 whitespace-normal break-words max-w-[160px]">{r.name}</td>
              <td className="px-3 py-1.5 uppercase text-zinc-400">{r.direction}</td>
              <td className="px-3 py-1.5 uppercase text-amber-400">{r.action}</td>
              <td className="px-3 py-1.5 uppercase text-cyan-400">{r.protocol}</td>
              <td className="px-3 py-1.5 text-zinc-300">{r.from_port}-{r.to_port}</td>
              <td className="px-3 py-1.5 text-zinc-500 whitespace-normal break-words max-w-[200px]">{r.native_id}</td>
            </>
          )}
        />
      )}
    </div>
  );
}

function DriftStat({ label, v, color }) {
  return (
    <div className={`border rounded-sm py-2 text-center ${color}`}>
      <div className="text-[9px] font-mono uppercase tracking-widest opacity-80">{label}</div>
      <div className="font-mono text-base">{v}</div>
    </div>
  );
}

function DriftSection({ title, rows, render }) {
  return (
    <div className="border border-zinc-800 rounded-sm overflow-hidden">
      <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-400 bg-zinc-900/60 border-b border-zinc-800 flex items-center gap-2">
        {title}
      </div>
      <div className="overflow-x-auto max-h-64">
        <table className="w-full text-xs">
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.policy_id || r.discover_id || i} className="border-t border-zinc-800/70 font-mono text-[11px]">
                {render(r)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BulkPushDialog({ open, onOpenChange, provider, policies }) {
  const relevant = (policies || []).filter((p) => !p.cloud || p.cloud === "any" || p.cloud === provider);
  const [picked, setPicked] = useState([]);

  useEffect(() => {
    if (open) setPicked([]);
  }, [open]);

  const togglePick = (id) => setPicked((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allOn = picked.length === relevant.length && relevant.length > 0;
  const toggleAll = () => setPicked(allOn ? [] : relevant.map((p) => p.policy_id));

  if (!open) return null;
  return (
    <BulkPushBody
      provider={provider}
      relevant={relevant}
      picked={picked}
      togglePick={togglePick}
      toggleAll={toggleAll}
      allOn={allOn}
      onOpenChange={onOpenChange}
    />
  );
}

function BulkPushBody({ provider, relevant, picked, togglePick, toggleAll, allOn, onOpenChange }) {
  // Two-stage: first pick, then DeployDialog handles the actual push + result rendering.
  const [confirm, setConfirm] = useState(false);
  if (confirm) {
    return (
      <DeployDialog
        open
        onOpenChange={onOpenChange}
        mode="policies-to-cloud"
        policyIds={picked}
        provider={provider}
      />
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => onOpenChange(false)}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm w-full max-w-xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display flex items-center gap-2"><Send className="w-4 h-4 text-emerald-400" /> Push policies to {provider.toUpperCase()}</div>
          <button onClick={toggleAll} className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-cyan-400">
            {allOn ? "deselect all" : "select all"}
          </button>
        </div>
        <div className="border border-zinc-800 rounded-sm max-h-80 overflow-y-auto">
          {relevant.length === 0 && <div className="text-zinc-500 font-mono text-xs p-6 text-center">no policies target {provider.toUpperCase()} or "any"</div>}
          {relevant.map((p) => (
            <label key={p.policy_id} className="flex items-center gap-3 px-3 py-2 border-b border-zinc-800/70 cursor-pointer hover:bg-zinc-900/60 last:border-b-0">
              <input
                type="checkbox"
                checked={picked.includes(p.policy_id)}
                onChange={() => togglePick(p.policy_id)}
                className="accent-cyan-500"
                data-testid={`bulkpush-pick-${p.policy_id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm whitespace-normal break-words">{p.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  {p.direction} · {p.action} · {p.protocol} · {p.port} · cloud {p.cloud}
                </div>
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            data-testid="bulkpush-next"
            disabled={picked.length === 0}
            onClick={() => setConfirm(true)}
            className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-sm"
          >NEXT ({picked.length})</Button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="border border-zinc-800 rounded-sm bg-zinc-950 p-2">
      <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono text-sm text-zinc-200 whitespace-normal break-words">{v ?? "—"}</div>
    </div>
  );
}
